"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import type { Route } from "next";
import { Button, Icon, Numeric } from "@wmart/ui";
import type {
  StorefrontBrandNode,
  StorefrontCategoryNode,
} from "@wmart/shared-types";

/**
 * Catalog left-sidebar — collapsible brand + category facets.
 *
 * Category is a department accordion: each top-level department is a
 * collapsible header (chevron + aggregate count); its sub-categories and leaves
 * render only when expanded, keeping the rail short. A department auto-opens
 * when it contains an active selection; the buyer can still fold/unfold it, and
 * a *collapsed* department holding a selection shows an accent dot so nothing is
 * hidden silently. Brands are a flat multi-select list capped to a preview with
 * a "Show all" toggle (selected brands are never hidden by the cap).
 *
 * Selection is multi-value (the api's `category_slug` / `brand` params accept
 * repeated values), so clicking a row toggles it in/out of the active set.
 * Picking any facet clears an active free-text search (one mode at a time).
 * Selecting at any tier filters to that category AND its descendants (the api
 * resolves descendants via a recursive CTE). Empty subtrees are pruned by the
 * api, so every row rendered here surfaces at least one product.
 */

const BRAND_PREVIEW = 8;

interface CatalogFiltersProps {
  categories: StorefrontCategoryNode[];
  /** Currently-selected category slugs (from the repeated `category_slug` URL param). Multi-select. */
  selectedCategorySlugs?: string[];
  /** Flat brand facets (from /v1/storefront/brands). Multi-select. */
  brands?: StorefrontBrandNode[];
  /** Currently-selected brand slugs (from the repeated `brand` URL param). */
  selectedBrandSlugs?: string[];
}

/** Does this category node or any of its descendants appear in the selected set? */
function subtreeHasSelection(
  node: StorefrontCategoryNode,
  selected: Set<string>,
): boolean {
  if (selected.has(node.slug)) return true;
  return node.children.some((c) => subtreeHasSelection(c, selected));
}

export function CatalogFilters({
  categories,
  selectedCategorySlugs = [],
  brands = [],
  selectedBrandSlugs = [],
}: CatalogFiltersProps) {
  const t = useTranslations("Catalog");
  const router = useRouter();
  const params = useSearchParams();
  const selectedBrandSet = new Set(selectedBrandSlugs);
  const selectedCategorySet = new Set(selectedCategorySlugs);

  // Per-department open override. Absent → follow the selection-aware default
  // (open iff the department contains a selection). A manual click stores an
  // ABSOLUTE open/closed value (not an inversion of the default), so selecting a
  // leaf inside a department never flips it shut, and a manual fold/unfold
  // survives later selection changes until the buyer toggles it again.
  const [openOverride, setOpenOverride] = useState<Map<string, boolean>>(
    new Map(),
  );
  const [showAllBrands, setShowAllBrands] = useState(false);

  const isDeptOpen = (dept: StorefrontCategoryNode): boolean => {
    const override = openOverride.get(dept.id);
    if (override !== undefined) return override;
    return subtreeHasSelection(dept, selectedCategorySet);
  };
  const toggleDeptOpen = (dept: StorefrontCategoryNode) => {
    const nextOpen = !isDeptOpen(dept);
    setOpenOverride((prev) => {
      const next = new Map(prev);
      next.set(dept.id, nextOpen);
      return next;
    });
  };

  // Category is multi-select: toggling a category adds/removes it from the
  // repeated `category_slug` query param. Picking a facet is "browse mode" and
  // clears any active free-text search — text and facets are mutually exclusive
  // (one mode at a time), so a stale search term can't silently zero results.
  const toggleCategory = (slug: string) => {
    const next = new URLSearchParams(params.toString());
    next.delete("cursor");
    next.delete("q");
    const set = new Set(next.getAll("category_slug"));
    next.delete("category_slug");
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    for (const s of set) next.append("category_slug", s);
    const q = next.toString();
    router.replace((q ? `/products?${q}` : "/products") as Route);
  };

  // Brand is multi-select: toggling a brand adds/removes it from the repeated
  // `brand` query param. Like category, picking a brand clears any active
  // free-text search (one mode at a time — see toggleCategory).
  const toggleBrand = (slug: string) => {
    const next = new URLSearchParams(params.toString());
    next.delete("cursor");
    next.delete("q");
    const set = new Set(next.getAll("brand"));
    next.delete("brand");
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    for (const s of set) next.append("brand", s);
    const q = next.toString();
    router.replace((q ? `/products?${q}` : "/products") as Route);
  };

  const hasAnyFilter =
    selectedCategorySlugs.length > 0 ||
    (params.get("q") ?? "").length > 0 ||
    selectedBrandSlugs.length > 0;

  // Cap the flat brand list to a preview, but never hide a selected brand.
  const collapseBrands = !showAllBrands && brands.length > BRAND_PREVIEW;
  const visibleBrands = (() => {
    if (!collapseBrands) return brands;
    const head = brands.slice(0, BRAND_PREVIEW);
    const headSlugs = new Set(head.map((b) => b.slug));
    const hiddenSelected = brands.filter(
      (b) => selectedBrandSet.has(b.slug) && !headSlugs.has(b.slug),
    );
    return [...head, ...hiddenSelected];
  })();

  return (
    <nav
      data-slot="catalog-filters"
      aria-label={t("filtersNavLabel")}
      className="flex flex-col gap-6"
    >
      {brands.length > 0 ? (
        <section className="flex flex-col gap-2">
          <SectionHeading>{t("facetBrand")}</SectionHeading>
          <ul role="list" className="flex flex-col gap-0.5">
            {visibleBrands.map((b) => (
              <li key={b.id} data-brand-row>
                <FilterRow
                  title={b.title}
                  count={b.product_count}
                  selected={selectedBrandSet.has(b.slug)}
                  indent={0}
                  onClick={() => toggleBrand(b.slug)}
                />
              </li>
            ))}
          </ul>
          {brands.length > BRAND_PREVIEW ? (
            <button
              type="button"
              onClick={() => setShowAllBrands((v) => !v)}
              className="self-start rounded-sm px-1 py-0.5 text-body-sm font-medium text-accent outline-none transition-colors duration-fast hover:text-accent-deep focus-visible:shadow-focus"
            >
              {showAllBrands ? t("showLess") : t("showAll", { count: brands.length })}
            </button>
          ) : null}
        </section>
      ) : null}

      <section
        className={
          brands.length > 0
            ? "flex flex-col gap-2 border-t border-ink-100 pt-5"
            : "flex flex-col gap-2"
        }
      >
        <SectionHeading>{t("facetCategory")}</SectionHeading>
        {categories.length === 0 ? (
          <p className="text-body-sm text-ink-500">{t("noCategories")}</p>
        ) : (
          <ul role="list" className="flex flex-col">
            {categories.map((dept) => (
              <li
                key={dept.id}
                className="border-b border-ink-100 last:border-b-0"
              >
                <DepartmentBlock
                  node={dept}
                  open={isDeptOpen(dept)}
                  onToggleOpen={() => toggleDeptOpen(dept)}
                  selectedSet={selectedCategorySet}
                  onSelect={toggleCategory}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {hasAnyFilter ? (
        <Button
          variant="link"
          size="sm"
          onClick={() => router.replace("/products" as Route)}
        >
          {t("clearFilters")}
        </Button>
      ) : null}
    </nav>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <p className="text-overline font-semibold uppercase tracking-[0.06em] text-ink-500">
      {children}
    </p>
  );
}

interface DepartmentBlockProps {
  node: StorefrontCategoryNode;
  open: boolean;
  onToggleOpen: () => void;
  selectedSet: Set<string>;
  onSelect: (slug: string) => void;
}

function DepartmentBlock({
  node,
  open,
  onToggleOpen,
  selectedSet,
  onSelect,
}: DepartmentBlockProps) {
  const t = useTranslations("Catalog");
  const hasChildren = node.children.length > 0;

  // A childless department isn't an accordion — its header row selects it
  // directly as a leaf-style filter.
  if (!hasChildren) {
    return (
      <FilterRow
        title={node.title}
        count={node.product_count}
        selected={selectedSet.has(node.slug)}
        indent={0}
        onClick={() => onSelect(node.slug)}
      />
    );
  }

  const hasSelection = subtreeHasSelection(node, selectedSet);

  return (
    <div>
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={open}
        aria-label={
          !open && hasSelection
            ? t("departmentAriaWithFilter", {
                title: node.title,
                count: node.product_count,
              })
            : t("departmentAria", {
                title: node.title,
                count: node.product_count,
              })
        }
        className="group flex w-full items-center gap-2 rounded-sm py-2 pe-2 ps-2 text-body font-medium text-ink-900 outline-none transition-colors duration-fast hover:bg-paper-warm focus-visible:shadow-focus"
      >
        <Icon
          name={open ? "chevronDown" : "chevronRight"}
          size={16}
          className="text-ink-400"
          aria-hidden
        />
        <span className="flex-1 truncate text-start">{node.title}</span>
        {!open && hasSelection ? (
          <span
            aria-hidden
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
          />
        ) : null}
        <Numeric
          className={[
            "shrink-0 text-caption tabular-nums",
            hasSelection ? "text-accent-deep" : "text-ink-400",
          ].join(" ")}
        >
          {node.product_count}
        </Numeric>
      </button>

      {open ? (
        <ul role="list" className="flex flex-col gap-0.5 pb-2">
          {node.children.map((sub) => (
            <li key={sub.id} className="flex flex-col">
              <FilterRow
                title={sub.title}
                count={sub.product_count}
                selected={selectedSet.has(sub.slug)}
                indent={1}
                onClick={() => onSelect(sub.slug)}
              />
              {sub.children.length > 0 ? (
                <ul role="list" className="flex flex-col">
                  {sub.children.map((leaf) => (
                    <li key={leaf.id}>
                      <FilterRow
                        title={leaf.title}
                        count={leaf.product_count}
                        selected={selectedSet.has(leaf.slug)}
                        indent={2}
                        onClick={() => onSelect(leaf.slug)}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface FilterRowProps {
  title: string;
  count: number;
  selected: boolean;
  /** 0 = top-level/brand, 1 = sub-category, 2 = leaf — drives the indent. */
  indent: 0 | 1 | 2;
  onClick: () => void;
}

function FilterRow({ title, count, selected, indent, onClick }: FilterRowProps) {
  // Indent grows with depth; aligned to sit under a department's title (past
  // the chevron). Selected rows carry the olive accent + a leading check.
  const indentCls = indent === 0 ? "ps-2" : indent === 1 ? "ps-9" : "ps-12";
  const textCls = indent === 2 ? "text-body-sm text-ink-500" : "text-body-sm";
  const stateCls = selected
    ? "bg-accent-soft text-accent-deep"
    : "text-ink-700 hover:bg-paper-warm hover:text-ink-900";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        "group flex w-full items-center justify-between gap-2",
        "rounded-sm py-1.5 pe-2",
        "outline-none transition-colors duration-fast",
        "focus-visible:shadow-focus",
        indentCls,
        textCls,
        stateCls,
      ].join(" ")}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {selected ? (
          <Icon
            name="check"
            size={14}
            className="text-accent-deep"
            aria-hidden
          />
        ) : null}
        <span className="truncate text-start">{title}</span>
      </span>
      <Numeric
        className={[
          "text-caption tabular-nums",
          selected ? "text-accent-deep" : "text-ink-400",
        ].join(" ")}
      >
        {count}
      </Numeric>
    </button>
  );
}
